import { X, Printer, FileDown } from "lucide-react";
import { toCSV, downloadCSV } from "../lib/csv";

const money = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;

export default function EstoqueReportModal({ open, onClose, titulo, escopo, itens, unidades = [], scope = "global" }) {
  if (!open) return null;

  const dt = new Date();
  const unitName = (uid) => unidades.find((u) => u.id === uid)?.nome || "—";

  // totais - usa custo médio ponderado da unidade (preco_custo)
  const custoOf = (e) => Number(e.preco_custo ?? e.preco_custo_global ?? 0);
  const totals = itens.reduce(
    (a, e) => {
      a.qtd += e.quantidade_atual;
      a.valorCusto += custoOf(e) * e.quantidade_atual;
      a.valorVenda += (e.preco_venda || 0) * e.quantidade_atual;
      return a;
    },
    { qtd: 0, valorCusto: 0, valorVenda: 0 }
  );

  const categorias = {};
  itens.forEach((i) => {
    const cat = i.categoria || "Geral";
    if (!categorias[cat]) categorias[cat] = { qtd: 0, valorCusto: 0, valorVenda: 0 };
    categorias[cat].qtd += i.quantidade_atual;
    categorias[cat].valorCusto += custoOf(i) * i.quantidade_atual;
    categorias[cat].valorVenda += (i.preco_venda || 0) * i.quantidade_atual;
  });

  const exportCSV = () => {
    const headers = [
      { label: "Unidade", get: (r) => unitName(r.unidade_id) },
      { label: "Produto", get: (r) => r.produto_nome },
      { label: "Categoria", get: (r) => r.categoria },
      { label: "Qtd", get: (r) => r.quantidade_atual },
      { label: "Custo Medio Unit", get: (r) => custoOf(r).toFixed(2).replace(".", ",") },
      { label: "Preco Venda Unit", get: (r) => Number(r.preco_venda || 0).toFixed(2).replace(".", ",") },
      { label: "Valor Custo Total", get: (r) => (custoOf(r) * r.quantidade_atual).toFixed(2).replace(".", ",") },
      { label: "Valor Venda Total", get: (r) => ((r.preco_venda || 0) * r.quantidade_atual).toFixed(2).replace(".", ",") },
    ];
    const csv = toCSV(itens, headers);
    downloadCSV(`estoque_${dt.toISOString().slice(0, 10)}.csv`, csv);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white border-4 border-neutral-900 w-full max-w-md max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b-2 border-neutral-900 print:hidden">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
              Relatório de estoque
            </p>
            <h3 className="font-display font-black text-lg tracking-tight">{titulo}</h3>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="estoque-csv-btn"
              onClick={exportCSV}
              className="bg-neutral-950 text-white h-10 px-4 font-display font-bold uppercase text-xs tracking-widest flex items-center gap-2"
            >
              <FileDown className="w-4 h-4" /> CSV
            </button>
            <button
              data-testid="estoque-imprimir-btn"
              onClick={() => window.print()}
              className="bg-yellow-400 text-neutral-950 h-10 px-4 font-display font-bold uppercase text-xs tracking-widest flex items-center gap-2"
            >
              <Printer className="w-4 h-4" /> Térmico
            </button>
            <button
              data-testid="estoque-fechar-btn"
              onClick={onClose}
              className="p-2 border-2 border-neutral-900 hover:bg-neutral-900 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-4 bg-neutral-50">
          <div id="thermal-receipt" className="thermal-receipt mx-auto border border-neutral-300">
            <div className="text-center">
              <div className="font-bold text-sm uppercase">RELATÓRIO DE ESTOQUE</div>
              <div className="text-[10px]">Gerado em {dt.toLocaleString("pt-BR")}</div>
            </div>
            <hr />
            <div className="text-[11px]">
              <div>ESCOPO: {escopo}</div>
              <div>ITENS: {itens.length}</div>
            </div>
            <hr />
            <div className="text-[11px]">
              <div className="font-bold">RESUMO POR CATEGORIA</div>
              {Object.entries(categorias).map(([cat, v]) => (
                <div key={cat} className="mt-1">
                  <div className="font-bold">{cat.toUpperCase()}</div>
                  <div className="flex justify-between">
                    <span>Unidades em estoque</span>
                    <span>{v.qtd}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Valor (venda)</span>
                    <span>{money(v.valorVenda)}</span>
                  </div>
                </div>
              ))}
            </div>
            <hr />
            <div className="text-[11px]">
              <div className="font-bold">ITENS</div>
              {itens.map((i, idx) => (
                <div key={idx} className="mt-1">
                  <div className="font-bold">{i.produto_nome}</div>
                  {scope === "global" && (
                    <div className="text-[10px]">UND: {unitName(i.unidade_id)}</div>
                  )}
                  <div className="flex justify-between">
                    <span>Qtd</span>
                    <span className="font-bold">{i.quantidade_atual}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Preco venda</span>
                    <span>{money(i.preco_venda)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Subtotal venda</span>
                    <span>{money((i.preco_venda || 0) * i.quantidade_atual)}</span>
                  </div>
                </div>
              ))}
            </div>
            <hr />
            <div className="text-[11px]">
              <div className="flex justify-between font-bold">
                <span>TOTAL UNIDADES</span>
                <span>{totals.qtd}</span>
              </div>
              <div className="flex justify-between font-bold">
                <span>VALOR (VENDA)</span>
                <span>{money(totals.valorVenda)}</span>
              </div>
              <div className="flex justify-between">
                <span>VALOR (CUSTO)</span>
                <span>{money(totals.valorCusto)}</span>
              </div>
              <div className="flex justify-between font-bold text-sm">
                <span>MARGEM POTENCIAL</span>
                <span>{money(totals.valorVenda - totals.valorCusto)}</span>
              </div>
            </div>
            <div className="text-center text-[10px] mt-3">--- FIM DO RELATÓRIO ---</div>
          </div>
        </div>
      </div>
    </div>
  );
}
